import { Form, Input, Modal } from 'antd';
import React from 'react';

import { useRenameFile } from '@/api';
import { usePermission } from '@/hooks/usePermission';
import { showMessage } from '@/shared/utils';

interface RenameFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  filePath: string;
  currentName: string;
}

export function RenameFileModal({
  isOpen,
  onClose,
  onSuccess,
  filePath,
  currentName,
}: RenameFileModalProps): React.JSX.Element {
  const [form] = Form.useForm();
  const { isMAAdmin } = usePermission();
  const [isLoading, setIsLoading] = React.useState(false);
  const renameFileMutation = useRenameFile();
  const [extension, setExtension] = React.useState('');

  React.useEffect(() => {
    if (isOpen) {
      const lastDot = currentName.lastIndexOf('.');
      if (lastDot > 0) {
        setExtension(currentName.substring(lastDot));
        form.setFieldsValue({ name: currentName.substring(0, lastDot) });
      } else {
        setExtension('');
        form.setFieldsValue({ name: currentName });
      }
    }
  }, [isOpen, currentName, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      const newName = values.name + extension;
      await renameFileMutation.mutateAsync({
        filePath,
        name: newName,
      });
      showMessage('success', 'Datei erfolgreich umbenannt');
      onSuccess();
      onClose();
    } catch (error: any) {
      if (error.response?.status === 409) {
        form.setFields([
          {
            name: 'name',
            errors: [error.response.data.message],
          },
        ]);
      } else {
        showMessage('error', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isMAAdmin) {
    return <></>;
  }

  return (
    <Modal
      title='Datei umbenennen'
      open={isOpen}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={isLoading}
      destroyOnHidden
    >
      <Form form={form} layout='vertical'>
        <Form.Item
          name='name'
          rules={[
            { required: true, message: 'Bitte geben Sie einen Dateinamen ein' },
            { max: 255, message: 'Dateiname darf nicht mehr als 255 Zeichen beinhalten' },
            {
              pattern: /^[^\\/:*?"<>|]+$/,
              message: 'Dateiname darf keine Sonderzeichen enthalten (\\ / : * ? " < > |)',
            },
          ]}
        >
          <Input addonAfter={extension} placeholder='Neuer Name' />
        </Form.Item>
      </Form>
    </Modal>
  );
}
